import { Component, ElementRef, Injector, Input, OnDestroy, Renderer2, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormArray, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { AbstractComponent, ConfirmationComponent, FirebaseService, RCSMessageTypeEnum, Traduct, UploadTypeEnum, UtilHelper } from 'lib-trend-core';
import {
  applyRcsTypeValidators,
  buildRcsCardGroup,
  buildRcsSuggestionGroup,
  RCS_CARD_DESCRIPTION_CHARACTER_LIMIT,
  RCS_CARD_TITLE_CHARACTER_LIMIT,
  RCS_MAX_CAROUSEL_CARDS,
  RCS_MAX_SUGGESTIONS,
  RCS_SUGGESTION_CHARACTER_LIMIT,
  RCS_TEXT_CHARACTER_LIMIT,
} from '../rcs-message-form.util';

/**
 * A variable the editor offers to insert as `{{token}}`. `labelKey` is translated; `label` is shown
 * as it is — a webhook offers the fields of its sample request, which are data, not UI text.
 */
export interface RcsVariableOption {
  token: string;
  labelKey?: string;
  label?: string;
  icon?: string;
}

/** Which upload area a file goes to: the standalone media, the rich card, or a carousel card. */
type UploadTarget = 'standalone' | 'richCard' | number;

const BYTES_IN_MB = 1024 * 1024;
const STANDALONE_IMAGE_MAX_SIZE = 2 * BYTES_IN_MB;
const STANDALONE_VIDEO_MAX_SIZE = 50 * BYTES_IN_MB;
const CARD_MEDIA_MAX_SIZE = 10 * BYTES_IN_MB;

/**
 * The RCS message editor — the six formats a campaign offers (text, image, video, PDF, rich card,
 * carousel), shared by the campaign wizard and the webhook integration
 * (SPEC-2026-10-rcs-webhook-integration-messages, plan AD-05).
 *
 * It edits the group it is given (built by `buildRcsDataGroup`) and keeps every piece of its own
 * state — the upload in flight, the emoji picker — per instance: a webhook page shows several
 * editors at once.
 */
@Component({
    selector: 'rcs-message-editor',
    templateUrl: './rcs-message-editor.component.html',
    styleUrls: ['./rcs-message-editor.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class RcsMessageEditorComponent extends AbstractComponent implements OnDestroy {

  /** The RCS message group. Not named `formGroup`: that would also bind Angular's FormGroupDirective. */
  @Input({ required: true }) group!: FormGroup;
  /** Read-only: an active webhook integration cannot be edited, uploads and add/remove included. */
  @Input() readonly = false;
  /** The storage folder the media go to. */
  @Input() uploadType: UploadTypeEnum = UploadTypeEnum.CAMPAING;
  /** The variables offered to the message text. Empty → no variable menu. */
  @Input() variables: RcsVariableOption[] = [];
  /** Whether the card titles, descriptions and suggestions offer the variables too. */
  @Input() cardVariables = false;

  @ViewChild('standaloneFile', { static: false }) standaloneFileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('richCardFile', { static: false }) richCardFileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('carouselFile', { static: false }) carouselFileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('emojiContainer', { static: false, read: ElementRef }) emojiContainer!: ElementRef;

  readonly TEXT_LIMIT = RCS_TEXT_CHARACTER_LIMIT;
  readonly CARD_TITLE_LIMIT = RCS_CARD_TITLE_CHARACTER_LIMIT;
  readonly CARD_DESCRIPTION_LIMIT = RCS_CARD_DESCRIPTION_CHARACTER_LIMIT;
  readonly SUGGESTION_LIMIT = RCS_SUGGESTION_CHARACTER_LIMIT;
  readonly MAX_SUGGESTIONS = RCS_MAX_SUGGESTIONS;
  readonly MAX_CAROUSEL_CARDS = RCS_MAX_CAROUSEL_CARDS;

  readonly formatTiles: ReadonlyArray<{ value: string; labelKey: string; label?: string; icon: string }> = [
    { value: RCSMessageTypeEnum.TEXT, labelKey: 'rcsMessage.editor.formatText', icon: 'mdi mdi-text' },
    { value: RCSMessageTypeEnum.IMAGE, labelKey: 'rcsMessage.editor.formatImage', icon: 'mdi mdi-image-outline' },
    { value: RCSMessageTypeEnum.VIDEO, labelKey: 'rcsMessage.editor.formatVideo', icon: 'mdi mdi-video-outline' },
    { value: RCSMessageTypeEnum.PDF, labelKey: '', label: 'PDF', icon: 'mdi mdi-file-pdf-box' },
    { value: RCSMessageTypeEnum.RICH_CARD, labelKey: 'rcsMessage.editor.formatCard', icon: 'mdi mdi-card-outline' },
    { value: RCSMessageTypeEnum.CAROUSEL, labelKey: 'rcsMessage.editor.formatCarousel', icon: 'mdi mdi-view-carousel-outline' },
  ];

  /** Upload areas with a file in flight — one entry per area, so one upload never spins another. */
  readonly uploading = new Set<UploadTarget>();
  private carouselUploadIndex = -1;

  showEmoji = false;
  translator!: Traduct;
  private emojiOutsideListener: (() => void) | null = null;
  private emojiTimeout: ReturnType<typeof setTimeout>;

  constructor(
    injector: Injector,
    private readonly firebaseService: FirebaseService,
    private readonly dialog: MatDialog,
    private readonly renderer: Renderer2,
  ) {
    super(injector);
  }

  override ngOnDestroy(): void {
    clearTimeout(this.emojiTimeout);
    this.emojiOutsideListener?.();
    super.ngOnDestroy();
  }

  // ------------------------------------------------------------------ format

  get type(): string {
    return this.group?.get('type')?.value;
  }

  isFormat(value: string): boolean {
    return this.type === value;
  }

  isMediaFormat(): boolean {
    return this.isFormat(RCSMessageTypeEnum.IMAGE) || this.isFormat(RCSMessageTypeEnum.VIDEO) || this.isFormat(RCSMessageTypeEnum.PDF);
  }

  /** A format the user picked: its validators apply and what belongs to the other formats goes. */
  onFormatChange(): void {
    applyRcsTypeValidators(this.group, { clear: true });
  }

  get richCard(): FormGroup {
    return this.group.get('richCard') as FormGroup;
  }

  get richCardSuggestions(): FormArray {
    return this.group.get('richCard.suggestions') as FormArray;
  }

  get carouselCards(): FormArray {
    return this.group.get('carousel') as FormArray;
  }

  suggestionsOf(card: AbstractControl): FormArray {
    return card.get('suggestions') as FormArray;
  }

  /** The example a suggestion value field shows, by the suggestion's type. */
  suggestionPlaceholder(suggestion: AbstractControl): string {
    const type = suggestion?.get('type')?.value;
    if (type === 'openUrl') {
      return 'https://exemplo.com';
    } else if (type === 'call') {
      return '5561999999999';
    } else if (type === 'reply') {
      return this.t('rcsMessage.editor.replyPlaceholder');
    }
    return '';
  }

  // ------------------------------------------------------------------ cards and suggestions

  addRichCardSuggestion(): void {
    if (this.readonly) return;
    this.richCardSuggestions.push(buildRcsSuggestionGroup());
  }

  /** Removes a suggestion of the rich card (`cardIndex` null) or of one carousel card. */
  removeSuggestion(cardIndex: number | null, index: number): void {
    const suggestions = cardIndex === null ? this.richCardSuggestions : this.suggestionsOf(this.carouselCards.at(cardIndex));
    this.confirm(() => suggestions.removeAt(index));
  }

  addCarouselCard(): void {
    if (this.readonly) return;
    this.carouselCards.push(buildRcsCardGroup());
  }

  removeCarouselCard(index: number): void {
    this.confirm(() => this.carouselCards.removeAt(index));
  }

  addCarouselSuggestion(cardIndex: number): void {
    if (this.readonly) return;
    this.suggestionsOf(this.carouselCards.at(cardIndex)).push(buildRcsSuggestionGroup());
  }

  // ------------------------------------------------------------------ media

  isUploading(target: UploadTarget): boolean {
    return this.uploading.has(target);
  }

  /** The accept list of the standalone file input, by format. */
  acceptFiles(): string {
    if (this.isFormat(RCSMessageTypeEnum.PDF)) {
      return 'application/pdf';
    } else if (this.isFormat(RCSMessageTypeEnum.VIDEO)) {
      return 'video/mp4, video/x-msvideo';
    } else if (this.isFormat(RCSMessageTypeEnum.IMAGE)) {
      return 'image/jpeg, image/png';
    }
    return '';
  }

  pickStandaloneFile(): void {
    if (this.readonly) return;
    this.standaloneFileInput?.nativeElement.click();
  }

  pickRichCardFile(): void {
    if (this.readonly) return;
    this.richCardFileInput?.nativeElement.click();
  }

  pickCarouselFile(cardIndex: number): void {
    if (this.readonly) return;
    this.carouselUploadIndex = cardIndex;
    this.carouselFileInput?.nativeElement.click();
  }

  onStandaloneFile(event: Event): void {
    this.upload(event, 'standalone', (url) => this.group.get('url').setValue(url));
  }

  onRichCardFile(event: Event): void {
    this.upload(event, 'richCard', (url, file) => {
      this.richCard.get('fileUrl').setValue(url);
      this.richCard.get('fileContentType').setValue(file.type);
    });
  }

  onCarouselFile(event: Event): void {
    const index = this.carouselUploadIndex;
    this.carouselUploadIndex = -1;
    if (index < 0) return;
    this.upload(event, index, (url, file) => {
      const card = this.carouselCards.at(index);
      card?.get('fileUrl').setValue(url);
      card?.get('fileContentType').setValue(file.type);
    });
  }

  removeStandaloneMedia(): void {
    this.confirm(() => this.group.get('url').setValue(null));
  }

  removeRichCardMedia(): void {
    this.confirm(() => this.richCard.get('fileUrl').setValue(null));
  }

  removeCarouselMedia(cardIndex: number): void {
    this.confirm(() => this.carouselCards.at(cardIndex)?.get('fileUrl')?.setValue(null));
  }

  /** A preview that fails to load leaves its field empty, so the user uploads it again. */
  onMediaLoadError(control: AbstractControl): void {
    control?.setValue(null);
  }

  private upload(event: Event, target: UploadTarget, apply: (url: string, file: File) => void): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) {
      return;
    }
    if (!this.isValidFileSize(file, target === 'standalone' ? 'standalone' : 'card')) {
      input.value = '';
      return;
    }

    this.uploading.add(target);
    this.firebaseService.uploadFile(file, new Date().getTime().toString(), this.uploadType)
      .then((snapshot) => apply(snapshot.url, file))
      .catch(() => this.alertService.error(this.t('rcsMessage.editor.uploadFailed')))
      .finally(() => {
        this.uploading.delete(target);
        input.value = '';
      });
  }

  private isValidFileSize(file: File, scope: 'standalone' | 'card'): boolean {
    const maxSize = this.maxFileSize(file, scope);

    if (maxSize !== null) {
      if (file.size > maxSize) {
        this.alertService.warning(
          this.t('rcsMessage.editor.fileSizeExceeded', { size: maxSize / BYTES_IN_MB }),
          this.t('rcsMessage.editor.invalidFileSizeTitle'),
        );
        return false;
      }
      return true;
    }

    const fileTypeLimit = UtilHelper.getFileTypeLimit(file.type);
    if (fileTypeLimit && file.size > fileTypeLimit.maxSize) {
      this.alertService.warning(fileTypeLimit.alertMessage, fileTypeLimit.alertTitle);
      return false;
    }

    return true;
  }

  private maxFileSize(file: File, scope: 'standalone' | 'card'): number | null {
    const isImage = file.type?.startsWith('image/');
    const isVideo = file.type?.startsWith('video/');

    if (scope === 'card') {
      return isImage || isVideo ? CARD_MEDIA_MAX_SIZE : null;
    }
    if (isImage) {
      return STANDALONE_IMAGE_MAX_SIZE;
    }
    if (isVideo) {
      return STANDALONE_VIDEO_MAX_SIZE;
    }
    return null;
  }

  // ------------------------------------------------------------------ toolbar

  /** Appends `{{token}}` to the field, as the campaign wizard always has. */
  insertVariable(control: AbstractControl, token: string): void {
    if (this.readonly || !control) return;
    control.setValue((control.value || '') + `{{${token}}}`);
    control.markAsDirty();
  }

  variableLabel(option: RcsVariableOption): string {
    return option.label ?? (option.labelKey ? this.t(option.labelKey) : option.token);
  }

  addEmoji(event: any): void {
    const message = this.group.get('message');
    message.setValue((message.value || '') + event.emoji.native);
  }

  toggleEmoji(): void {
    if (this.readonly) return;
    clearTimeout(this.emojiTimeout);
    this.showEmoji = !this.showEmoji;
    if (this.showEmoji) {
      // Next tick: the click that opened the picker must not be the one that closes it.
      this.emojiTimeout = setTimeout(() => this.listenEmojiOutsideClick());
    } else {
      this.closeEmoji();
    }
  }

  private listenEmojiOutsideClick(): void {
    this.emojiOutsideListener?.();
    this.emojiOutsideListener = this.renderer.listen('document', 'click', (event: MouseEvent) => {
      const container = this.emojiContainer?.nativeElement as HTMLElement;
      if (this.showEmoji && container && !container.contains(event.target as Node)) {
        this.closeEmoji();
      }
    });
  }

  private closeEmoji(): void {
    this.showEmoji = false;
    this.emojiOutsideListener?.();
    this.emojiOutsideListener = null;
  }

  private confirm(action: () => void): void {
    if (this.readonly) return;
    this.dialog.open(ConfirmationComponent, { width: '600px' }).afterClosed().subscribe((result) => {
      if (Boolean(result) === true) {
        action();
      }
    });
  }
}
